import { ConfirmModal } from '@chiroup/components/ConfirmModal';
import { LoadingPage } from '@chiroup/components/LoadingPage';
import { Toggle } from '@chiroup/components/form/Toggle';
import { FeatureFlags, FlagGroups, flagGroupsArr } from '@chiroup/core/constants/flags';
import { useState } from 'react';
import useAdminMe from '../../hooks/useAdminMe';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import Header from '../layout/Header';
import FlagsNavigation from './FlagsNavigation';
import useFlags from './hooks/useFlags';

const FLAG_OWNER_EMAIL = 'k.tegtmeier@chiroup.com';

type PendingFlagChange = {
  flag: string;
  flagGroup: string;
  value: boolean;
};

const Flags = () => {
  const { data, isFetching, updateFlag } = useFlags();
  const { hasPermission } = useAdminPermissions();
  const { data: me } = useAdminMe();
  const canEditFlags = hasPermission('flags.edit');
  const [pendingChange, setPendingChange] = useState<PendingFlagChange | null>(null);

  const requiresConfirmation =
    import.meta.env.VITE_STAGE === 'prod' && me?.email?.toLowerCase() !== FLAG_OWNER_EMAIL;

  const onToggle = (flag: string, flagGroup: string, value: boolean) => {
    if (!canEditFlags) return;
    if (flagGroup === FlagGroups.everyone && requiresConfirmation) {
      setPendingChange({ flag, flagGroup, value });
    } else {
      updateFlag(flag, flagGroup, value);
    }
  };

  return (
    <>
      <Header title="Feature flags" />
      {hasPermission('clinic.view') && <FlagsNavigation active="flags" />}
      {isFetching ? (
        <LoadingPage />
      ) : (
        <div className="m-6">
          <div className="mt-8 flex flex-col">
            <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                <div className="overflow-hidden shadow ring-1 ring-black/5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                        >
                          Flag
                        </th>
                        {flagGroupsArr.map(flagGroup => (
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {flagGroup.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-300 bg-white">
                      {data?.map(flag => (
                        <tr key={flag.flag}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                            {flag.flag === FeatureFlags.massTexting
                              ? 'Patient Campaigns'
                              : flag.name}
                          </td>
                          {flagGroupsArr.map(flagGroup => (
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              <Toggle
                                value={flag.flagGroups?.includes(flagGroup.value)}
                                onChange={val => {
                                  onToggle(flag.flag, flagGroup.value, val);
                                }}
                                disabled={!canEditFlags}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={!!pendingChange}
        confirm={() => {
          if (pendingChange) {
            updateFlag(pendingChange.flag, pendingChange.flagGroup, pendingChange.value);
          }
          setPendingChange(null);
        }}
        close={() => setPendingChange(null)}
        title="Feature flag change"
        description="Only Kate should be turning on or off feature flags for this environment. Are you sure you want to go around the process?"
        confirmText="Yes"
      />
    </>
  );
};

export default Flags;
